<template>
  <q-card style="max-width: 400px; width: 100%; background-color: transparent" flat>
    <q-card-section v-if="showTitle" class="q-pa-none q-pb-md">
      <div class="text-h5">{{ formTitle || 'Dynamic Form' }}</div>
    </q-card-section>
    <q-form class="q-gutter-md" @submit.prevent="onSubmit">
      <div class="form-field" style="text-align: center">
        <q-img
          v-if="formImageHeaderUrl"
          :src="formImageHeaderUrl"
          style="border-radius: 8px; cursor: pointer"
          fit="contain"
        />

        <q-avatar
          class="dotted-border radius-10 default-img"
          size="100px"
          icon="image"
          text-color="black"
          v-if="!showButtons && formImageHeaderUrl == ''"
        />
      </div>
      <template v-for="(field, index) in formFields || []" :key="field._id || field.field_name || index">
        <!-- Text Input -->
        <div v-if="field.option_type === 'text'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-input dense v-model="previewData[getFieldKey(field)]" :readonly="readonly" :disable="readonly" />
        </div>

        <!-- Email Input -->
        <div v-else-if="field.option_type === 'email'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-input
            dense
            v-model="previewData[getFieldKey(field)]"
            type="email"
            :readonly="readonly"
            :disable="readonly"
          />
        </div>

        <!-- Textarea -->
        <div v-else-if="field.option_type === 'textarea'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-input
            dense
            v-model="previewData[getFieldKey(field)]"
            type="textarea"
            :readonly="readonly"
            :disable="readonly"
          />
        </div>

        <!-- Date Input -->
        <div v-else-if="field.option_type === 'date'" class="form-field">
          <!-- Label -->
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>

          <!-- Description -->
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>

          <!-- Native date input styled like q-input -->
          <div class="date-input-wrapper">
            <input
              type="date"
              v-model="previewData[getFieldKey(field)]"
              :readonly="readonly"
              :disabled="readonly"
              class="date-input"
            />
            <!-- <span class="date-icon">📅</span> -->
          </div>
        </div>
        <!-- Time Input -->
        <div v-else-if="field.option_type === 'time'" class="form-field">
          <!-- Label -->
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>

          <!-- Description -->
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>

          <!-- Styled input like Quasar -->
          <div class="time-input-wrapper">
            <input
              type="time"
              v-model="previewData[getFieldKey(field)]"
              :readonly="readonly"
              :disabled="readonly"
              class="time-input"
            />
            <!-- <span class="time-icon">⏰</span> -->
          </div>
        </div>

        <!-- Prefecture Select -->
        <div v-else-if="field.option_type === 'prefecture'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-select
            dense
            v-model="previewData[getFieldKey(field)]"
            :options="prefectureOptions"
            :readonly="readonly"
            :disable="readonly"
          />
        </div>

        <!-- Postcode Input -->
        <div v-else-if="field.option_type === 'postcode'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-input
            dense
            v-model="previewData[getFieldKey(field)]"
            :mask="field.contents?.is_hyphenated ? '###-####' : '#######'"
            :readonly="readonly"
            :disable="readonly"
          />
        </div>

        <!-- Phone Number Input -->
        <div v-else-if="field.option_type === 'phone_number'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-input
            type="tel"
            dense
            v-model="previewData[getFieldKey(field)]"
            :mask="field.contents?.is_hyphenated ? '###-####-####' : '###########'"
            :readonly="readonly"
            :disable="readonly"
          />
        </div>

        <!-- Select Dropdown -->
        <div v-else-if="field.option_type === 'select'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-select
            dense
            v-model="previewData[getFieldKey(field)]"
            :options="field.contents?.options || []"
            :readonly="readonly"
            :disable="readonly"
          />
        </div>

        <!-- Radio Options -->
        <div v-else-if="field.option_type === 'radio'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-option-group
            dense
            v-model="previewData[getFieldKey(field)]"
            :options="(field.contents?.options || []).map((opt, index) => ({ label: opt, value: `${opt}_${index}` }))"
            color="primary"
            type="radio"
            :disable="readonly"
          />
        </div>

        <!-- Checkbox Options -->
        <div v-else-if="field.option_type === 'checkbox'" class="form-field">
          <div class="field-label">
            <span>{{ field.field_name }}</span>
            <span v-if="field.is_required" class="text-negative">*</span>
          </div>
          <div v-if="field.contents?.description" class="field-description text-caption text-grey-6">
            {{ field.contents.description }}
          </div>
          <q-option-group
            dense
            v-model="previewData[getFieldKey(field)]"
            :options="(field.contents?.options || []).map((opt, index) => ({ label: opt, value: `${opt}_${index}` }))"
            color="primary"
            type="checkbox"
            :disable="readonly"
          />
        </div>
      </template>

      <!-- Submit and Reset Buttons (only show if interactive) -->
      <div class="row justify-end q-gutter-sm" v-if="!readonly && showButtons">
        <q-btn type="button" class="btn-cancel" flat no-caps @click="onReset">
          {{ $t('cancel') }}
        </q-btn>
        <q-btn type="button" class="btn-save" :loading="submitting" @click="onSubmit">
          {{ $t('save') }}
        </q-btn>
      </div>
    </q-form>
  </q-card>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { PREFECTURES } from '@/utils/constants'
import { IDynamicLiffFormField } from '@/utils/types'

@Options({
  components: {},
  emits: ['update:modelValue', 'submit', 'reset'],
})
export default class FormPreview extends Vue {
  @Prop({ default: () => [] })
  formFields!: IDynamicLiffFormField[]

  @Prop({ default: '' })
  formTitle!: string

  @Prop({ default: false })
  readonly!: boolean

  @Prop({ default: true })
  showTitle!: boolean

  @Prop({ default: false })
  showButtons!: boolean

  @Prop({ default: false })
  submitting!: boolean

  @Prop({ default: () => ({}) })
  modelValue!: Record<string, any>

  @Prop()
  formImageHeaderUrl!: string

  formData: Record<string, any> = {}
  previewData: Record<string, any> = {} // Use field ID as key instead of index
  fieldKeyMap: Map<any, string> = new Map() // Map each field reference to its unique key
  nextUniqueId = 0 // Counter for generating unique IDs
  isInitializing = false
  isUpdatingFromParent = false

  get prefectureOptions() {
    return PREFECTURES
  }

  // Get unique key for field - use _id if available, otherwise generate stable unique key
  getFieldKey(field: IDynamicLiffFormField): string {
    const existingKey = this.fieldKeyMap.get(field)
    if (existingKey) {
      return existingKey
    }

    // Generate new key: prefer _id, otherwise create unique key
    let key: string
    if (field._id) {
      key = field._id
    } else {
      // Create a truly unique key that won't collide
      key = `field_${this.nextUniqueId++}_${Date.now()}`
    }

    this.fieldKeyMap.set(field, key)
    return key
  }

  @Watch('formFields', { immediate: true, deep: true })
  onFormFieldsChange() {
    this.isInitializing = true
    this.initializeFormData()
    this.initializePreviewData()
    this.$nextTick(() => {
      this.isInitializing = false
    })
  }

  @Watch('modelValue', { immediate: true, deep: true })
  onModelValueChange() {
    if (this.modelValue && !this.isInitializing) {
      this.isUpdatingFromParent = true
      this.formData = { ...this.modelValue }
      const existingPreviewData = { ...this.previewData }
      this.initializePreviewData(existingPreviewData)
      this.$nextTick(() => {
        this.isUpdatingFromParent = false
      })
    }
  }

  @Watch('previewData', { deep: true })
  onPreviewDataChange() {
    if (!this.isInitializing && !this.isUpdatingFromParent && this.modelValue !== undefined) {
      // Only emit updates if modelValue is provided (submission mode) and not updating from parent
      this.formFields.forEach((field) => {
        const fieldKey = this.getFieldKey(field)
        this.formData[fieldKey] = this.previewData[fieldKey]
      })

      this.$emit('update:modelValue', this.formData)
    }
  }

  initializeFormData() {
    if (!this.formFields || this.formFields.length === 0) {
      this.formData = {}
      return
    }

    // Initialize form data with default values based on field types
    const newFormData = this.formFields.reduce((data, field) => {
      const fieldKey = this.getFieldKey(field)
      const existingValue = this.formData[fieldKey]

      if (existingValue !== undefined) {
        data[fieldKey] = existingValue
      } else {
        switch (field.option_type) {
          case 'checkbox':
            data[fieldKey] = []
            break
          case 'select':
          case 'radio':
          case 'prefecture':
            data[fieldKey] = ''
            break
          default:
            data[fieldKey] = ''
        }
      }
      return data
    }, {} as Record<string, any>)

    this.formData = newFormData
  }

  initializePreviewData(existingData: Record<string, any> = {}) {
    if (!this.formFields || this.formFields.length === 0) {
      this.previewData = {}
      return
    }

    // Initialize preview data with default values, preserving existing user input
    const newPreviewData = this.formFields.reduce((data, field) => {
      const fieldKey = this.getFieldKey(field)

      // Use existing data if provided, otherwise preserve current preview data
      if (existingData[fieldKey] !== undefined) {
        data[fieldKey] = existingData[fieldKey]
      } else if (this.previewData[fieldKey] !== undefined) {
        data[fieldKey] = this.previewData[fieldKey]
      } else {
        // Set default value for new field
        switch (field.option_type) {
          case 'checkbox':
            data[fieldKey] = []
            break
          case 'select':
          case 'radio':
          case 'prefecture':
            data[fieldKey] = ''
            break
          default:
            data[fieldKey] = ''
        }
      }
      return data
    }, {} as Record<string, any>)

    this.previewData = newPreviewData
  }

  onSubmit() {
    // Clean the form data by removing index suffixes from radio/checkbox values
    const cleanedFormData = this.cleanFormData(this.formData)
    console.log('cleanedFormData', cleanedFormData)

    this.$emit('submit', cleanedFormData)
  }

  // Clean form data by removing index suffixes from option values
  cleanFormData(data: Record<string, any>): Record<string, any> {
    const cleaned = { ...data }

    this.formFields.forEach((field) => {
      const fieldKey = this.getFieldKey(field)
      const value = cleaned[fieldKey]

      if (value !== undefined && (field.option_type === 'radio' || field.option_type === 'checkbox')) {
        if (field.option_type === 'radio') {
          // For radio, remove the index suffix to get the original value
          if (typeof value === 'string' && value.includes('_')) {
            const lastUnderscoreIndex = value.lastIndexOf('_')
            const originalValue = value.substring(0, lastUnderscoreIndex)
            cleaned[fieldKey] = originalValue
          }
        } else if (field.option_type === 'checkbox') {
          // For checkbox, it's an array, so clean each value
          if (Array.isArray(value)) {
            cleaned[fieldKey] = value.map((val) => {
              if (typeof val === 'string' && val.includes('_')) {
                const lastUnderscoreIndex = val.lastIndexOf('_')
                return val.substring(0, lastUnderscoreIndex)
              }
              return val
            })
          }
        }
      }
    })

    return cleaned
  }

  onReset() {
    this.initializeFormData()
    this.initializePreviewData() // Reset doesn't preserve data
    this.$emit('reset')
  }
}
</script>

<style scoped lang="scss">
.form-field {
  margin-bottom: 16px;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.field-description {
  margin-bottom: 3px;
  line-height: 1.4;
}

.form-field {
  :deep(.q-field--dense .q-field__native) {
    padding: 12px !important;
  }

  :deep(.q-select .q-field__native) {
    padding: 12px !important;
  }

  // Radio and Checkbox styling - each option has individual white background
  :deep(.q-option-group) {
    .q-radio,
    .q-checkbox {
      background-color: white;
      border: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0.24);
      border-radius: 0;
      padding: 8px 12px;
      margin-bottom: 6px;
      width: 100%;
      display: flex;
    }

    .q-checkbox .q-checkbox__inner {
      opacity: 0.6;
      width: 16px !important;
      height: 16px !important;
      min-width: 16px !important;
    }

    .q-checkbox__inner--truthy {
      opacity: 0.8 !important;
    }

    .q-checkbox__bg {
      border-width: 1.5px !important;
    }

    .q-radio__inner {
      width: 14px !important;
      height: 14px !important;
      min-width: 14px !important;
    }
  }

  :deep(.q-field--type-date .q-field__append),
  :deep(.q-field--type-time .q-field__append) {
    .q-icon {
      font-size: 20px !important;
    }
  }

  :deep(input[type='date']::-webkit-calendar-picker-indicator),
  :deep(input[type='time']::-webkit-calendar-picker-indicator) {
    width: 20px;
    height: 20px;
  }
}
/* wrapper styled like q-input */
.time-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
}

/* native time input styled to match */
.time-input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  background: transparent;
}

// /* icon styled like q-icon append */
// .time-icon {
//   margin-left: 6px;
//   color: #666;
//   font-size: 16px;
//   cursor: default;
//   user-select: none;
// }

.date-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
}

.date-input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  background: transparent;
}

// .date-icon {
//   margin-left: 6px;
//   color: #666;
//   font-size: 16px;
//   cursor: default;
//   user-select: none;
// }
</style>
